import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";
import { NEW_PLANS, PRICING_PATHS, TEAM_LIMITS, changesAnnounced, CHANGE_DATE_ISO } from "@/content/pricing/plans";
import { fill, formatCount, formatStorage, formatUsd, usePricingCopy } from "@/content/pricing";
import { DOWNLOAD_PATH, FIND_PATH, LOGIN_PATH, REGISTER_PATH, STUDIO_APP_NOTIFY } from "./paths";
import { CameraIcon, PRODUCT_NAME, ProductBadge, SearchIcon } from "./products";
import { STUDIO_APP_SCREENS, STUDIO_APP_SHOTS, type StudioShot } from "./studioAppShots";
import ContentImage from "@/components/ContentImage";
import { GuideImg } from "@/components/guides/GuideBody";
import { GUIDE_IMAGES } from "@/content/guides/images";
import { guidePath } from "@/content/guides";
import { BAND, BTN_OUTLINE, BTN_PRIMARY, CARD, EYEBROW, FOCUS, H2, LEAD, SECTION_Y, TEXT_LINK, WRAP } from "./ui";

// The Studio page's own sections (src/pages/home/StudioPage.tsx): what's in Studio, the Studio app (coming in
// November 2026; Studio is in the browser today), and the Studio plans. Copy is home.studio.* (English until translated: i18next falls back to it); the plan figures come from
// src/content/pricing/plans.ts and their words from the pricing copy (English, Spanish, Portuguese), so the numbers
// can't drift from /pricing.

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 flex-none">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

const Icon = ({ d }: { d: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

const PILLARS = [
  { id: "galleries", link: "/for-photographers", icon: <Icon d="M4 5h16v14H4zM4 15l4-4 4 4 3-3 5 5M15 9.5h.01" /> },
  { id: "selling", link: "/for-photographers", icon: <Icon d="M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0" /> },
  { id: "find", link: FIND_PATH, icon: <SearchIcon size={22} /> },
  { id: "live", link: WEB_UPLOADER_PATH, icon: <Icon d="M12 16V4M7 9l5-5 5 5M4 20h16" /> },
] as const;

/** What's in Studio: galleries, selling, Find your photos, live upload. */
export const StudioPillars = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.studio.${key}`);
  return (
    <section id="whats-in-studio" aria-labelledby="studio-pillars" className={`${BAND} scroll-mt-4 border-y border-border`} data-testid="studio-pillars">
      <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
      <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
        <div className="flex max-w-[640px] flex-col gap-3">
          <span className={EYEBROW}>{PRODUCT_NAME.studio}</span>
          <h2 id="studio-pillars" className={H2}>
            {s("pillars.heading")}
          </h2>
        </div>
        <p className={`${LEAD} max-w-[30em]`}>{s("pillars.text")}</p>
      </div>
      <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(250px,100%),1fr))] gap-5 p-0">
        {PILLARS.map((p) => (
          <li key={p.id} className={`${CARD} flex flex-col gap-3 p-7`} data-testid={`pillar-${p.id}`}>
            <span className="text-accent-foreground">{p.icon}</span>
            <h3 className="m-0 font-display text-[21px] font-semibold">{s(`pillars.${p.id}.title`)}</h3>
            <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{s(`pillars.${p.id}.text`)}</p>
            <Link to={p.link} className={`${TEXT_LINK} mt-auto self-start`}>
              {t("home.business.learnMore")} <span aria-hidden="true">→</span>
              <span className="sr-only">: {s(`pillars.${p.id}.title`)}</span>
            </Link>
          </li>
        ))}
      </ul>
      </div>
    </section>
  );
};

const PLATFORMS = ["mac", "ios", "windows"] as const;
const APP_FEATURES = [
  { id: "live", icon: <CameraIcon size={22} /> },
  { id: "upload", icon: <Icon d="M12 16V4M7 9l5-5 5 5M4 20h16" /> },
  { id: "orders", icon: <Icon d="M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0" /> },
  { id: "today", icon: <Icon d="M4 20V10M10 20V4M16 20v-7M22 20H2" /> },
  { id: "admin", icon: <Icon d="M4 6h16M4 12h16M4 18h10" /> },
] as const;

/** A browser window, for "Today: Studio runs in your web browser". */
const BrowserIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="4" width="19" height="16" rx="2.5" />
    <path d="M2.5 8.5h19M6 6.3h.01M8.5 6.3h.01" />
  </svg>
);

/** A Mac laptop around an app window: the screen in a near-black bezel, on a thin silver base, with a soft, cool
 *  shadow that suits the light grounds. */
const MacFrame = ({ shot, alt, sizes = "(min-width: 1320px) 940px, 80vw", testId = "studio-app-mac" }: { shot: StudioShot; alt: string; sizes?: string; testId?: string }) => (
  <div data-testid={testId}>
    <div className="rounded-[14px] bg-[#0E1117] p-[1.6%] pb-[2.6%] shadow-[0_2px_4px_rgba(18,23,33,0.10),0_28px_60px_rgba(18,23,33,0.20),inset_0_0_0_1.5px_#2B313C]">
      <img
        src={shot.src}
        srcSet={shot.srcSet}
        sizes={sizes}
        width={shot.width}
        height={shot.height}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="block h-auto w-full rounded-[4px] bg-[#F3F4F6]"
      />
    </div>
    <div aria-hidden="true" className="relative mx-[-5%] h-[clamp(8px,1.1vw,14px)] rounded-b-[14px] bg-gradient-to-b from-[#E4E7EC] to-[#A9AFB9] shadow-[0_10px_24px_rgba(18,23,33,0.16)]">
      <span className="absolute left-1/2 top-0 h-[40%] w-[14%] -translate-x-1/2 rounded-b-md bg-[#C3C8D0]" />
    </div>
  </div>
);

/** An iPhone around an app screen (the screen has its own status bar and Dynamic Island). */
const IPhoneFrame = ({ shot, alt, sizes = "(min-width: 1024px) 210px, 22vw", testId = "studio-app-phone" }: { shot: StudioShot; alt: string; sizes?: string; testId?: string }) => (
  <div className="rounded-[17%/7.8%] bg-[#0E1117] p-[3.4%] shadow-[0_1px_2px_rgba(18,23,33,0.12),0_18px_40px_rgba(18,23,33,0.22),inset_0_0_0_1.5px_#2B313C]" data-testid={testId}>
    <img
      src={shot.src}
      srcSet={shot.srcSet}
      sizes={sizes}
      width={shot.width}
      height={shot.height}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="block h-auto w-full rounded-[13.5%/6.2%] bg-[#F3F4F6]"
    />
  </div>
);

/**
 * The Studio app showcase (#studio-app): coming in November 2026, and Studio in the browser today. The badge and the
 * "Today" line say it before anything else, so nobody looks for a download: Mac and iPhone/iPad launch in November
 * 2026, Windows later (/download). Then what's in the app (live capture, upload, orders and refunds, Today, the full
 * admin) and real app screens in a Mac and an iPhone (./studioAppShots). The actions: start free or sign in, in the
 * browser; and "Get notified when the app launches" (an email to support).
 */
export const StudioAppSection = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.studio.app.${key}`);
  return (
    <section id="studio-app" aria-labelledby="studio-app-heading" className={`${BAND} scroll-mt-4 border-y border-border`} data-testid="studio-app">
      <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-[clamp(40px,5vw,64px)]`}>
        <div className="flex flex-wrap items-start gap-x-16 gap-y-8">
          <div className="flex min-w-0 flex-[1.1_1_420px] flex-col gap-6">
            <span className="inline-flex flex-wrap items-center gap-3">
              <ProductBadge product="studio" />
              <span className={`${EYEBROW} text-muted-foreground`}>{s("eyebrow")}</span>
            </span>
            <span className="inline-flex h-9 items-center gap-2 self-start rounded-full border-[1.5px] border-accent-foreground bg-accent px-3.5 text-sm font-bold text-accent-foreground" data-testid="studio-app-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3.5" y="5" width="17" height="15" rx="2" />
                <path d="M3.5 10h17M8 3v4M16 3v4" />
              </svg>
              {s("badge")}
            </span>
            <h2 id="studio-app-heading" className={H2}>
              {s("heading")}
            </h2>
            <p className={`${LEAD} max-w-[34em]`}>{s("text")}</p>
          </div>
          <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-5 lg:pt-[52px]">
            <div className={`${CARD} flex items-start gap-3.5 p-5`} data-testid="studio-app-today">
              <span className="mt-0.5 flex-none text-accent-foreground">
                <BrowserIcon />
              </span>
              <span className="flex flex-col gap-1">
                <strong className="text-base font-bold leading-snug text-foreground">{s("todayTitle")}</strong>
                <span className="text-[15px] leading-relaxed text-muted-foreground">{s("todayText")}</span>
              </span>
            </div>
            <ul className="m-0 flex list-none flex-wrap gap-2.5 p-0" aria-label={s("platformsLabel")} data-testid="studio-app-platforms">
              {PLATFORMS.map((p) => {
                const later = p === "windows";
                return (
                  <li key={p} className={`inline-flex h-9 items-center gap-2 rounded-full border bg-background px-3.5 text-sm font-semibold ${later ? "border-dashed border-input text-muted-foreground" : "border-border text-foreground"}`}>
                    {s(p)}
                    <span className={`text-xs font-semibold uppercase tracking-[0.08em] ${later ? "text-muted-foreground" : "text-accent-foreground"}`}>{s(later ? "later" : "launch")}</span>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <Link to={REGISTER_PATH} className={BTN_PRIMARY} data-testid="studio-app-start">
                {s("startBrowser")}
              </Link>
              <Link to={LOGIN_PATH} className={BTN_OUTLINE}>
                {t("home.doors.studio.signIn")}
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-0">
              <a href={STUDIO_APP_NOTIFY} className={`${TEXT_LINK} gap-2`} data-testid="studio-app-notify">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9zM10 20a2.2 2.2 0 0 0 4 0" />
                </svg>
                {s("notify")}
              </a>
              <Link to={DOWNLOAD_PATH} className={`inline-flex min-h-11 items-center gap-1.5 rounded-md font-semibold text-foreground underline-offset-4 hover:underline ${FOCUS}`} data-testid="studio-app-details">
                {s("details")} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
        {/* The app itself: a Mac with the iPhone in front of its right corner. */}
        <figure className="m-0 flex flex-col items-center gap-4" data-testid="studio-app-shots">
          <div className="relative w-full max-w-[1080px] pb-[4%] pr-[12%] sm:pr-[10%]">
            <MacFrame shot={STUDIO_APP_SHOTS.mac} alt={s("macAlt")} />
            <div className="absolute bottom-0 right-0 w-[22%] max-w-[220px] sm:w-[19%]">
              <IPhoneFrame shot={STUDIO_APP_SHOTS.phone} alt={s("phoneAlt")} />
            </div>
          </div>
          <figcaption className="max-w-[46em] text-center text-sm leading-relaxed text-muted-foreground">{s("shotCaption")}</figcaption>
        </figure>
        <div className="flex flex-col gap-5">
          <h3 className="m-0 font-display text-[clamp(21px,2vw,26px)] font-bold tracking-[-0.02em]">{s("featuresLabel")}</h3>
          {/* Live capture and Upload lead (two wide cards), then orders, Today and the admin: 2 + 3 from 1024 px, two
              columns on a tablet (the last one full width), one on a phone. */}
          <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-6" data-testid="studio-app-features">
            {APP_FEATURES.map((f, i) => (
              <li
                key={f.id}
                className={`${CARD} flex flex-col gap-2.5 p-6 ${i < 2 ? "lg:col-span-3" : i === 4 ? "sm:col-span-2 lg:col-span-2" : "lg:col-span-2"}`}
                data-testid={`studio-app-feature-${f.id}`}
              >
                <span className="text-accent-foreground">{f.icon}</span>
                <h4 className="m-0 font-display text-[19px] font-semibold">{s(`features.${f.id}.title`)}</h4>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{s(`features.${f.id}.text`)}</p>
              </li>
            ))}
          </ul>
        </div>
        <StudioAppScreens />
      </div>
    </section>
  );
};

const SCREENS = [
  { id: "today", shot: STUDIO_APP_SCREENS.macToday },
  { id: "galleries", shot: STUDIO_APP_SCREENS.macGalleries },
  { id: "orders", shot: STUDIO_APP_SCREENS.macOrders },
] as const;
const PHONES = [
  { id: "phoneUploadAlt", shot: STUDIO_APP_SCREENS.phoneUpload },
  { id: "phoneGalleriesAlt", shot: STUDIO_APP_SCREENS.phoneGalleries },
  { id: "phoneOrdersAlt", shot: STUDIO_APP_SCREENS.phoneOrders },
] as const;

/** "Inside the Studio app" (#studio-app-screens): real app screens, three Mac windows and three iPhone screens. */
const StudioAppScreens = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.studio.screens.${key}`);
  return (
    <div id="studio-app-screens" className="flex scroll-mt-4 flex-col gap-6" data-testid="studio-app-screens">
      <div className="flex flex-col gap-2">
        <h3 className="m-0 font-display text-[clamp(21px,2vw,26px)] font-bold tracking-[-0.02em]">{s("heading")}</h3>
        <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{s("text")}</p>
      </div>
      <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-9 p-0 md:grid-cols-3">
        {SCREENS.map((x) => (
          <li key={x.id} className="flex flex-col gap-4" data-testid={`studio-app-screen-${x.id}`}>
            <div className="px-[5%]">
              <MacFrame shot={x.shot} alt={s(`${x.id}.alt`)} sizes="(min-width: 768px) 30vw, 92vw" testId={`studio-app-screen-${x.id}-mac`} />
            </div>
            <span className="flex flex-col gap-1 pt-1">
              <strong className="font-display text-[18px] font-semibold">{s(`${x.id}.title`)}</strong>
              <span className="text-[15px] leading-relaxed text-muted-foreground">{s(`${x.id}.text`)}</span>
            </span>
          </li>
        ))}
      </ul>
      <figure className="m-0 flex flex-col items-center gap-4">
        <ul className="m-0 grid w-full max-w-[720px] list-none grid-cols-3 gap-[clamp(12px,3vw,32px)] p-0">
          {PHONES.map((x) => (
            <li key={x.id}>
              <IPhoneFrame shot={x.shot} alt={s(x.id)} sizes="(min-width: 768px) 210px, 30vw" testId="studio-app-screen-phone" />
            </li>
          ))}
        </ul>
        <figcaption className="text-center text-sm text-muted-foreground">
          <strong className="font-semibold text-foreground">{s("phones")}</strong> · {s("credit")}
        </figcaption>
      </figure>
    </div>
  );
};

const UPLOAD_POINTS = ["fast", "resume", "dedupe", "previews", "where", "live"] as const;

/** "Built to replace FTP and FileZilla" (#studio-upload): what the Studio app's uploader does, with its Upload screen. */
export const StudioUploadSection = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.studio.upload.${key}`);
  return (
    <section id="studio-upload" aria-labelledby="studio-upload-heading" className="scroll-mt-4" data-testid="studio-upload">
      <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-10`}>
        <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
          <div className="flex max-w-[640px] flex-col gap-3">
            <span className={EYEBROW}>{s("eyebrow")}</span>
            <h2 id="studio-upload-heading" className={H2}>
              {s("heading")}
            </h2>
          </div>
          <p className={`${LEAD} max-w-[34em]`}>{s("text")}</p>
        </div>
        <div className="grid items-start gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <div className="px-[5%]">
            <MacFrame shot={STUDIO_APP_SCREENS.macUpload} alt={s("shotAlt")} sizes="(min-width: 1024px) 600px, 92vw" testId="studio-upload-mac" />
          </div>
          <ul className="m-0 grid list-none grid-cols-1 gap-x-8 gap-y-6 p-0 sm:grid-cols-2">
            {UPLOAD_POINTS.map((id) => (
              <li key={id} className="flex flex-col gap-1.5 border-t-2 border-border pt-3" data-testid={`studio-upload-${id}`}>
                <h3 className="m-0 font-display text-[18px] font-semibold">{s(`points.${id}.title`)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{s(`points.${id}.text`)}</p>
              </li>
            ))}
          </ul>
        </div>
        <p className="m-0 flex flex-wrap items-center gap-x-5 gap-y-2 text-[15px] leading-relaxed text-muted-foreground">
          <span className="max-w-[52em]">{s("browser")}</span>
          <Link to={WEB_UPLOADER_PATH} className={TEXT_LINK}>
            {s("browserLink")} <span aria-hidden="true">→</span>
          </Link>
        </p>
      </div>
    </section>
  );
};

const AT_WORK = [
  { id: "gallery", media: "event-gallery-photo-grid" },
  { id: "search", media: "selfie-search-results" },
  { id: "insights", media: "dashboard-sales-insights" },
] as const;

/** "Studio at work" (#studio-at-work): real screens of Studio in the browser, from upload to sale and team payouts. */
export const StudioAtWork = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.studio.atWork.${key}`);
  const split = GUIDE_IMAGES["split-list"];
  return (
    <section id="studio-at-work" aria-labelledby="studio-at-work-heading" className="scroll-mt-4" data-testid="studio-at-work">
      <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-10`}>
        <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
          <div className="flex max-w-[640px] flex-col gap-3">
            <span className={EYEBROW}>{s("eyebrow")}</span>
            <h2 id="studio-at-work-heading" className={H2}>
              {s("heading")}
            </h2>
          </div>
          <p className={`${LEAD} max-w-[30em]`}>{s("text")}</p>
        </div>
        <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-10 p-0 md:grid-cols-3">
          {AT_WORK.map((x) => (
            <li key={x.id} className="flex flex-col gap-4" data-testid={`studio-at-work-${x.id}`}>
              <div className="aspect-[16/10] overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
                <ContentImage name={x.media} sizes="(min-width: 768px) 30vw, 92vw" className="block h-full w-full object-cover object-left-top" />
              </div>
              <h3 className="m-0 font-display text-[19px] font-semibold">{s(`${x.id}.title`)}</h3>
              <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{s(`${x.id}.text`)}</p>
            </li>
          ))}
        </ul>
        {split && (
          <div className={`${CARD} grid items-center gap-x-10 gap-y-6 p-[clamp(20px,3vw,32px)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]`} data-testid="studio-at-work-split">
            <div className="flex flex-col gap-3">
              <h3 className="m-0 font-display text-[clamp(21px,2vw,26px)] font-bold tracking-[-0.02em]">{s("split.title")}</h3>
              <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{t("home.studio.atWork.split.text", { plan: NEW_PLANS.find((p) => p.id === "max")?.name })}</p>
              <Link to={guidePath("split-gallery-revenue")} className={`${TEXT_LINK} self-start`}>
                {s("split.link")} <span aria-hidden="true">→</span>
              </Link>
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-white">
              <GuideImg img={split} alt={s("split.alt")} sizes="(min-width: 1024px) 600px, 92vw" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const planFeatureLabel = (f: string | { text: string }) => (typeof f === "string" ? f : f.text);

/** The Studio plans (Lite, Pro, Max). Photohawk Personal has no pricing on the site until it's decided. */
export const StudioPlans = () => {
  const { t } = useTranslation();
  const { copy, numberLocale } = usePricingCopy();
  const s = (key: string, opts?: Record<string, string | number>) => t(`home.studio.pricing.${key}`, opts ?? {});
  const now = Date.now();
  const beforeChange = now < Date.parse(`${CHANGE_DATE_ISO}T00:00:00Z`);
  const plans = copy.pricing.plans;
  return (
    <section id="pricing" aria-labelledby="studio-pricing" className="scroll-mt-4" data-testid="studio-pricing">
      <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
        <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
          <div className="flex max-w-[680px] flex-col gap-3">
            <span className="inline-flex flex-wrap items-center gap-3">
              <ProductBadge product="studio" />
              <span className={EYEBROW}>{s("eyebrow")}</span>
            </span>
            <h2 id="studio-pricing" className={H2}>
              {s("heading")}
            </h2>
          </div>
          <p className={`${LEAD} max-w-[30em]`}>{s("text")}</p>
        </div>
        {beforeChange && (
          <p className="m-0 self-start rounded-full border border-border bg-muted px-4 py-1.5 text-sm text-muted-foreground" data-testid="studio-plans-from">
            {s("fromChange")}
          </p>
        )}
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-4 p-0">
          {NEW_PLANS.map((plan) => {
            const team = TEAM_LIMITS[plan.id];
            const featured = !!plan.featured;
            return (
              <li
                key={plan.id}
                className={cn(CARD, "flex flex-col gap-3 p-6", featured && "border-2 border-[#EA8A23] shadow-md")}
                data-testid={`studio-plan-${plan.id}`}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="font-display text-2xl font-bold">{plan.name}</span>
                  {featured && <span className="rounded-full bg-[#EA8A23] px-2.5 py-0.5 text-xs font-bold text-[#121721]">{copy.common.mostPopular}</span>}
                </span>
                <span className="flex items-baseline gap-1">
                  <span className="font-display text-[40px] font-bold leading-none">{formatUsd(plan.priceUsd, numberLocale)}</span>
                  <span className="text-muted-foreground">{copy.common.perMonth}</span>
                </span>
                <span className="text-sm leading-relaxed text-muted-foreground">{copy.common.planTaglines[plan.id]}</span>
                <ul className="m-0 flex list-none flex-col gap-2 border-t border-border p-0 pt-3 text-sm leading-normal [&_svg]:text-accent-foreground">
                  <li className="flex gap-2">
                    <Check />
                    {fill(plans.sellingLine, { rate: `${Math.round(plan.commissionRate * 100)}%` })}
                  </li>
                  <li className="flex gap-2">
                    <Check />
                    {fill(plans.storageLine, { storage: formatStorage(plan.storageGb, copy, numberLocale) })}
                  </li>
                  <li className="flex gap-2">
                    <Check />
                    {plans.freeDownloadsLabel}: {fill(plans.freeDownloadsLine, { count: formatCount(plan.freeDownloads, numberLocale) })}
                  </li>
                  <li className="flex gap-2">
                    <Check />
                    {team === null ? s("teamUnlimited") : s("team", { count: team })}
                  </li>
                  {/* Branding: Lite's "Your own branding, “Powered by Photohawk”", Pro's "Your own branding", Max's "Everything in Pro". */}
                  <li className="flex gap-2" data-testid={`studio-plan-${plan.id}-branding`}>
                    <Check />
                    {planFeatureLabel(copy.common.planFeatures[plan.id][0])}
                  </li>
                </ul>
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link to={REGISTER_PATH} className={BTN_PRIMARY}>
            {t("home.startFree")}
          </Link>
          {/* Every Studio feature, plan by plan: the table on the new pricing page (these plans, not today's /pricing). */}
          <Link
            to={`${PRICING_PATHS.pricing}#compare-features`}
            className={TEXT_LINK}
            data-testid="studio-compare-features"
          >
            {copy.pricing.compare.linkLabel} <span aria-hidden="true">→</span>
          </Link>
          {beforeChange && changesAnnounced(now) && (
            <Link to={PRICING_PATHS.changes} className={TEXT_LINK}>
              {t("footer.pricingChanges")} <span aria-hidden="true">→</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
